import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useMemo,
} from 'react';
import { useQuery, useQueryClient } from 'react-query';
import { useProjectContext } from './ProjectContext';
import * as notificationApi from '../services/notificationApi';
import type { AppNotification, NotificationListResponse } from '../services/types';
import { SERVICE_BASES } from '../services/apiClient';
import { useAuth } from './AuthContext';
import { createResilientEventSource } from '../utils/resilientEventSource';

/** Legacy SSE log lines from the mapping engine coordinator (not generic pipeline work). */
export function isMappingEngineLogLine(logLine?: string | null): boolean {
  return (logLine ?? '').toLowerCase().includes('generating mapping');
}

/** Structured mapping progress from SSE (emitted by coordinator). */
export interface MappingProgressState {
  type: 'mapping_start' | 'mapping_table_progress' | 'mapping_complete';
  /** The batch this mapping progress belongs to. */
  batchId: string;
  totalTables: number;
  totalChunks: number;
  completedChunks: number;
  completedTables: string[];
  tableNames: string[];
  /** Progress percent from the backend (35-85 range during mapping). */
  progressPercent: number;
  /** Summary stats populated on mapping_complete. */
  totalFieldMappings?: number;
  avgConfidence?: number;
}

/** Generic pipeline progress from SSE (profiling, semantic summary, etc.). */
export interface PipelineProgressState {
  progressPercent: number;
  logLine: string;
  entityId: string;
  updatedAt: number;
}

/** Live export progress from SSE. */
export interface ExportProgressState {
  batchId: string;
  tablesCompleted: number;
  tablesTotal: number;
  progressPercent: number;
  logLine?: string;
  jobId?: string;
}

/** Live schedule run progress from SSE (tenant-scoped channel). */
export interface ScheduleProgressState {
  scheduleId: string;
  runId: string;
  phase: string;
  phaseStatus: 'started' | 'completed' | 'failed' | 'skipped';
  batchId?: string;
  progressPercent: number;
  metrics: Record<string, any>;
  logLine?: string;
}

/**
 * Live pre-mapping readiness progress streamed from the
 * ``agent:{session_id}`` SSE channel during a /readiness run. Each
 * session gets its own slot so two readiness runs in parallel never
 * bleed into one another. The PreMappingReadinessStep subscribes to
 * its session's slot and renders live "Checking table X of Y" copy.
 */
export interface ReadinessProgressState {
  sessionId: string;
  phase: 'starting' | 'analyzing' | 'complete';
  tablesAnalyzed: number;
  tablesTotal: number;
  totalColumns: number;
  currentTable?: string;
  blockingCount: number;
  advisoryCount: number;
  updatedAt: number;
}

function dropPipelineProgressSlot(
  prev: Record<string, PipelineProgressState>,
  batchId: string,
): Record<string, PipelineProgressState> {
  if (!(batchId in prev)) return prev;
  const next = { ...prev };
  delete next[batchId];
  return next;
}

interface NotificationContextValue {
  notifications: AppNotification[];
  unreadCount: number;
  isLoading: boolean;
  markRead: (notificationId: string) => void;
  markAllRead: () => void;
  latestToast: AppNotification | null;
  dismissToast: () => void;
  showToast: (title: string, message: string, severity?: 'success' | 'error' | 'info' | 'warning') => void;
  /** Live mapping progress from SSE stream, keyed by batchId. */
  mappingProgressMap: Record<string, MappingProgressState>;
  /** Clear mapping progress for a specific batch (e.g. after the widget has been dismissed). */
  clearMappingProgress: (batchId: string) => void;
  /**
   * Generic pipeline progress (profiling, semantic summary, etc.), keyed by
   * entity_id (typically batchId). Each batch gets its own slot so progress
   * from one batch never bleeds into another batch's UI.
   */
  pipelineProgressMap: Record<string, PipelineProgressState>;
  /** Live schedule run progress from SSE. Null when no schedule run is active. */
  scheduleProgress: ScheduleProgressState | null;
  /** Live export progress from SSE, keyed by batchId. */
  exportProgressMap: Record<string, ExportProgressState>;
  /** Live readiness-detector progress from SSE, keyed by sessionId. */
  readinessProgressMap: Record<string, ReadinessProgressState>;
  /** Drop the readiness progress slot for a session (after dialog dismiss). */
  clearReadinessProgress: (sessionId: string) => void;
  /** Whether the notification SSE stream is connected. */
  sseConnected: boolean;
}

const NotificationContext = createContext<NotificationContextValue | undefined>(
  undefined
);

export const NotificationProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { selectedProjectId } = useProjectContext();
  const { isAuthenticated } = useAuth();
  const queryClient = useQueryClient();
  const [latestToast, setLatestToast] = useState<AppNotification | null>(null);
  const [mappingProgressMap, setMappingProgressMap] = useState<Record<string, MappingProgressState>>({});
  const clearMappingProgress = useCallback((batchId: string) => {
    setMappingProgressMap((prev) => {
      const next = { ...prev };
      delete next[batchId];
      return next;
    });
  }, []);
  const [pipelineProgressMap, setPipelineProgressMap] = useState<Record<string, PipelineProgressState>>({});
  const [scheduleProgress, setScheduleProgress] = useState<ScheduleProgressState | null>(null);
  const [exportProgressMap, setExportProgressMap] = useState<Record<string, ExportProgressState>>({});
  const [readinessProgressMap, setReadinessProgressMap] = useState<Record<string, ReadinessProgressState>>({});
  const clearReadinessProgress = useCallback((sessionId: string) => {
    setReadinessProgressMap((prev) => {
      if (!(sessionId in prev)) return prev;
      const next = { ...prev };
      delete next[sessionId];
      return next;
    });
  }, []);
  const [sseConnected, setSseConnected] = useState(false);

  // REST poll: initial load + 60s fallback
  const { data, isLoading } = useQuery<NotificationListResponse>(
    ['notifications', selectedProjectId],
    () => notificationApi.getNotifications(selectedProjectId),
    {
      enabled: isAuthenticated,
      staleTime: 30_000,
      refetchInterval: 60_000,
    }
  );

  // SSE connection scoped to the current user via the HttpOnly auth cookie.
  // resilientEventSource sets `withCredentials: true` so the cookie flows.
  useEffect(() => {
    if (!isAuthenticated) return;

    const handle = createResilientEventSource({
      urlBuilder: () => `${SERVICE_BASES.gateway}/api/v1/notifications/stream`,
      onConnected: () => {
        setSseConnected(true);
      },
      onDisconnected: () => {
        setSseConnected(false);
      },
      onReconnected: () => {
        setSseConnected(true);
        // Invalidate caches to catch events missed during disconnect
        queryClient.invalidateQueries(['notifications', selectedProjectId]);
        queryClient.invalidateQueries(['batches']);
      },

      onMessage: (event) => {
      try {
        const data = JSON.parse(event.data);

        // Schedule run progress event (tenant-scoped SSE channel)
        if (data.category === 'schedule_progress') {
          setScheduleProgress({
            scheduleId: data.schedule_id,
            runId: data.run_id,
            phase: data.phase,
            phaseStatus: data.phase_status,
            batchId: data.batch_id,
            progressPercent: data.progress_percent ?? 0,
            metrics: data.metrics || {},
            logLine: data.log_line,
          });
          // Invalidate run history so polling picks up the new phase
          queryClient.invalidateQueries(['schedule-runs', data.schedule_id]);
          // Clear schedule progress when run completes
          if (data.phase === 'completed' || data.phase_status === 'failed') {
            setTimeout(() => setScheduleProgress(null), 3000);
          }
          return;
        }

        // Pre-mapping readiness progress event (per-session, no DB persistence)
        if (data.category === 'readiness_progress') {
          const sessionId = data.session_id as string | undefined;
          if (!sessionId) return;
          const meta = (data.metadata || {}) as Record<string, any>;
          const eventType = data.event_type as string;
          setReadinessProgressMap((prev) => {
            const prior = prev[sessionId];
            const next: ReadinessProgressState = {
              sessionId,
              phase: 'analyzing',
              tablesAnalyzed: prior?.tablesAnalyzed ?? 0,
              tablesTotal:
                meta.total_target_tables ?? prior?.tablesTotal ?? 0,
              totalColumns:
                meta.total_target_columns ?? prior?.totalColumns ?? 0,
              blockingCount: prior?.blockingCount ?? 0,
              advisoryCount: prior?.advisoryCount ?? 0,
              updatedAt: Date.now(),
            };
            if (eventType === 'readiness_start') {
              next.phase = 'starting';
            } else if (eventType === 'readiness_table_progress') {
              next.tablesAnalyzed = meta.tables_analyzed ?? next.tablesAnalyzed;
              next.tablesTotal = meta.tables_total ?? next.tablesTotal;
              next.currentTable = meta.table;
            } else if (eventType === 'readiness_complete') {
              next.phase = 'complete';
              next.blockingCount = meta.blocking_count ?? 0;
              next.advisoryCount = meta.advisory_count ?? 0;
            }
            return { ...prev, [sessionId]: next };
          });
          return;
        }

        // Transient progress event — just invalidate status cache, no notification
        if (data.category === 'progress') {
          if (data.entity_id) {
            queryClient.invalidateQueries(['status', data.entity_id]);
          }

          // Capture structured mapping progress metadata. Drives the
          // useMappingWorkflow materialization trigger when the engine
          // emits a ``mapping_complete`` event; the chat-panel widget
          // that used to render this was retired in favor of the unified
          // ProgressStrip / ProgressSheet (per-session channel).
          const meta = data.metadata;
          const progressBatchId = data.entity_id ?? '';

          // Generic pipeline progress (profiling, semantic summary, etc.) is
          // scoped by entity_id and explicitly excludes events that belong to
          // a dedicated widget (mapping, export). Without this exclusion the
          // pipeline slot would be double-written by mapping events and would
          // retain stale "Generating mapping…" log lines after the dedicated
          // mapping widget has cleared, leaking across batches/projects.
          const SPECIALIZED_PROGRESS_TYPES = new Set([
            'mapping_start',
            'mapping_table_progress',
            'mapping_complete',
            'export_table_progress',
            // Pre-mapping readiness events: the Readiness step in the
            // Generate Mapping dialog renders its own progress, so we
            // mustn't surface these in the generic pipeline slot.
            'readiness_start',
            'readiness_table_progress',
            'readiness_tool_call',
            'readiness_complete',
          ]);
          const isSpecialized = meta?.type && SPECIALIZED_PROGRESS_TYPES.has(meta.type);
          const isMappingLog = isMappingEngineLogLine(data.log_line);
          if (progressBatchId && data.log_line && !isSpecialized && !isMappingLog) {
            setPipelineProgressMap((prev) => ({
              ...prev,
              [progressBatchId]: {
                progressPercent: data.progress_percent ?? 0,
                logLine: data.log_line,
                entityId: progressBatchId,
                updatedAt: Date.now(),
              },
            }));
          }
          // Clear legacy pipeline slot when a task finishes. Mapping engine
          // reports completion at ~85% with metadata.type = mapping_complete,
          // so percent >= 100 alone leaves stale "Generating mapping…" bars.
          const MAPPING_PROGRESS_TYPES = new Set([
            'mapping_start',
            'mapping_table_progress',
            'mapping_complete',
          ]);
          const isMappingProgressEvent =
            Boolean(meta?.type) && MAPPING_PROGRESS_TYPES.has(meta.type);
          if (
            progressBatchId &&
            (data.progress_percent >= 100 || isMappingProgressEvent)
          ) {
            setPipelineProgressMap((prev) => dropPipelineProgressSlot(prev, progressBatchId));
          }

          if (!progressBatchId) {
            // Drop mapping events without a batch identity
          } else if (meta?.type === 'mapping_start') {
            setPipelineProgressMap((prev) => dropPipelineProgressSlot(prev, progressBatchId));
            setMappingProgressMap((prev) => ({
              ...prev,
              [progressBatchId]: {
                type: 'mapping_start',
                batchId: progressBatchId,
                totalTables: meta.total_tables ?? 0,
                totalChunks: meta.total_chunks ?? 0,
                completedChunks: 0,
                completedTables: [],
                tableNames: meta.table_names ?? [],
                progressPercent: data.progress_percent ?? 35,
              },
            }));
          } else if (meta?.type === 'mapping_table_progress') {
            setMappingProgressMap((prev) => ({
              ...prev,
              [progressBatchId]: {
                type: 'mapping_table_progress',
                batchId: progressBatchId,
                totalTables: meta.total_tables ?? prev[progressBatchId]?.totalTables ?? 0,
                totalChunks: meta.total_chunks ?? prev[progressBatchId]?.totalChunks ?? 0,
                completedChunks: meta.completed_chunks ?? 0,
                completedTables: meta.completed_tables ?? [],
                tableNames: prev[progressBatchId]?.tableNames ?? [],
                progressPercent: data.progress_percent ?? 50,
              },
            }));
          } else if (meta?.type === 'mapping_complete') {
            const completedBatchId = progressBatchId;
            setPipelineProgressMap((prev) => dropPipelineProgressSlot(prev, completedBatchId));
            setMappingProgressMap((prev) => ({
              ...prev,
              [completedBatchId]: {
                type: 'mapping_complete',
                batchId: completedBatchId,
                totalTables: prev[completedBatchId]?.totalTables ?? 0,
                totalChunks: prev[completedBatchId]?.totalChunks ?? 0,
                completedChunks: prev[completedBatchId]?.totalChunks ?? 0,
                completedTables: prev[completedBatchId]?.tableNames ?? [],
                tableNames: prev[completedBatchId]?.tableNames ?? [],
                progressPercent: data.progress_percent ?? 85,
                totalFieldMappings: meta.total_field_mappings,
                avgConfidence: meta.avg_confidence,
              },
            }));
            // Auto-clear completed mapping progress after 5s
            setTimeout(() => {
              setMappingProgressMap((prev) => {
                const next = { ...prev };
                delete next[completedBatchId];
                return next;
              });
            }, 5000);
          }

          // Export progress tracking
          if (meta?.type === 'export_table_progress' && progressBatchId) {
            setExportProgressMap((prev) => ({
              ...prev,
              [progressBatchId]: {
                batchId: progressBatchId,
                tablesCompleted: meta.tables_completed ?? 0,
                tablesTotal: meta.tables_total ?? 0,
                progressPercent: data.progress_percent ?? 0,
                logLine: data.log_line,
                jobId: meta.job_id,
              },
            }));
            // Auto-clear when export reaches 100%
            if (data.progress_percent >= 100) {
              setTimeout(() => {
                setExportProgressMap((prev) => {
                  const next = { ...prev };
                  delete next[progressBatchId];
                  return next;
                });
              }, 5000);
            }
          }

          return;
        }

        const notification: AppNotification = data;

        // Optimistic update of the React Query cache
        queryClient.setQueryData<NotificationListResponse>(
          ['notifications', selectedProjectId],
          (prev) => {
            if (!prev) {
              return {
                notifications: [{ ...notification, is_read: false }],
                unread_count: 1,
              };
            }
            return {
              notifications: [
                { ...notification, is_read: false },
                ...prev.notifications,
              ].slice(0, 100),
              unread_count: prev.unread_count + 1,
            };
          }
        );

        setLatestToast(notification);

        // Invalidate related entity caches so detail pages update instantly
        if (notification.entity_type === 'batch' && notification.entity_id) {
          queryClient.invalidateQueries(['batch', notification.entity_id]);
          queryClient.invalidateQueries(['status', notification.entity_id]);
          queryClient.invalidateQueries(['mappings', notification.entity_id]);
        }

        // W4: Invalidate project sources when profiling completes so the
        // canvas picks up newly profiled sources without waiting for polling.
        if (
          notification.category === 'batch' &&
          notification.project_id &&
          (notification.title === 'New Sources Profiled' || notification.title === 'Profiling Complete')
        ) {
          queryClient.invalidateQueries(['projectSources', notification.project_id]);
        }

        // Export completion: invalidate workspace so meta refetch picks up final status
        if (notification.title === 'Export Complete' || notification.title === 'Export Failed') {
          queryClient.invalidateQueries({ predicate: (q) => {
            const key = q.queryKey[0];
            return key === 'agent-workspace' || key === 'agent-session' || key === 'batches' || key === 'projectSources';
          }});
          // Clear export progress
          if (notification.entity_id) {
            setExportProgressMap((prev) => {
              const next = { ...prev };
              delete next[notification.entity_id!];
              return next;
            });
          }
        }

        // Execution/schedule completion: invalidate related caches
        if (notification.title === 'Execution Complete' || notification.title === 'Execution Failed') {
          queryClient.invalidateQueries(['execution-runs']);
        }
        if (notification.title === 'Scheduled Run Complete' || notification.title === 'Scheduled Run Failed') {
          queryClient.invalidateQueries(['schedule-runs']);
          queryClient.invalidateQueries(['batches']);
        }
      } catch {
        // Ignore malformed messages
      }
      },
    });

    return () => {
      handle.close();
      setSseConnected(false);
      setMappingProgressMap({});
      setPipelineProgressMap({});
      setExportProgressMap({});
    };
  }, [selectedProjectId, queryClient, isAuthenticated]);

  const markRead = useCallback(
    async (notificationId: string) => {
      await notificationApi.markNotificationRead(notificationId);
      queryClient.invalidateQueries(['notifications', selectedProjectId]);
    },
    [selectedProjectId, queryClient]
  );

  const markAllRead = useCallback(async () => {
    await notificationApi.markAllNotificationsRead();
    queryClient.invalidateQueries(['notifications', selectedProjectId]);
  }, [selectedProjectId, queryClient]);

  const dismissToast = useCallback(() => setLatestToast(null), []);

  const showToast = useCallback((title: string, message: string, severity: 'success' | 'error' | 'info' | 'warning' = 'success') => {
    setLatestToast({
      notification_id: `local-${Date.now()}`,
      title,
      message,
      severity,
      category: 'validation',
      user_id: 'local',
      tenant_id: 'local',
      project_id: null,
      metadata: {},
      is_read: false,
      created_at: new Date().toISOString(),
    } as AppNotification);
  }, []);

  const value = useMemo<NotificationContextValue>(
    () => ({
      notifications: data?.notifications ?? [],
      unreadCount: data?.unread_count ?? 0,
      isLoading,
      markRead,
      markAllRead,
      latestToast,
      dismissToast,
      showToast,
      mappingProgressMap,
      clearMappingProgress,
      pipelineProgressMap,
      scheduleProgress,
      exportProgressMap,
      readinessProgressMap,
      clearReadinessProgress,
      sseConnected,
    }),
    [data, isLoading, markRead, markAllRead, latestToast, dismissToast, showToast, mappingProgressMap, clearMappingProgress, pipelineProgressMap, scheduleProgress, exportProgressMap, readinessProgressMap, clearReadinessProgress, sseConnected]
  );

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotifications = (): NotificationContextValue => {
  const ctx = useContext(NotificationContext);
  if (!ctx) {
    throw new Error(
      'useNotifications must be used within a NotificationProvider'
    );
  }
  return ctx;
};
